import useSWRMutation from 'swr/mutation';
import { nanoid } from 'nanoid';
import {
  API_DELETE_SECTION,
  API_UPDATE_LAYOUT,
  API_REVERT_UNPUBLISHED_CHANGES,
  API_GET_LAYOUT
} from '../../../api/configurationScreenServices';
import { toastMessageConstants } from '../../../constants/globalConstant';
import cloneDeep from 'lodash.clonedeep';

// Merge a layout PUT response into the SWR layout cache without refetching
// the layout + every widget metavalue (old Redux flow used the PUT response).
const mergeLayoutCache = (currentData, layoutData) => {
  if (!layoutData) return currentData;
  const base = currentData || {};
  const sections =
    (layoutData.hasDraftChanges
      ? layoutData.draftLayout?.sections
      : layoutData.mobileLayout?.sections) ||
    base.sections ||
    [];
  return {
    ...base,
    ...layoutData,
    sections
  };
};

export function useSectionMutations(homepageId, previewPaneData) {
  const layoutKey = `/homepages/${homepageId}/layout`;

  const {
    trigger: addSection,
    isMutating: isAddingSection,
    error: addSectionError
  } = useSWRMutation(
    layoutKey,
    async () => {
      const { sections } = previewPaneData;
      const _sections = cloneDeep(sections);
      const newSection = {
        sectionId: `section-${nanoid()}`,
        rows: []
      };
      _sections.push(newSection);

      // Update layout
      const response = await API_UPDATE_LAYOUT(_sections, homepageId);
      if (response?.status !== 200) {
        const errorMessage = response?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      return {
        sections: _sections,
        selectedSection: sections.length,
        layout: response?.data
      };
    },
    {
      populateCache: (result, currentData) => mergeLayoutCache(currentData, result?.layout),
      revalidate: false,
      throwOnError: false
    }
  );

  const {
    trigger: addTopFixedSection,
    isMutating: isAddingTopSection,
    error: addTopSectionError
  } = useSWRMutation(
    layoutKey,
    async () => {
      const { sections } = previewPaneData;
      const _sections = cloneDeep(sections);
      const newSection = {
        sectionId: `section-${nanoid()}`,
        rows: [],
        isFixed: true
      };
      _sections.unshift(newSection);

      return {
        sections: _sections,
        selectedSection: 0
      };
    },
    {
      // Local-only change (no API call) — a revalidation here would wipe
      // the just-added fixed section from the preview.
      revalidate: false,
      populateCache: false,
      throwOnError: false
    }
  );

  const {
    trigger: deleteSection,
    isMutating: isDeletingSection,
    error: deleteSectionError
  } = useSWRMutation(
    layoutKey,
    async (key, { arg: { sectionIndex, shouldUpdateLayout = false } }) => {
      const { sections } = previewPaneData;
      const sectionId = sections[sectionIndex].sectionId;

      // Update layout first if dirty
      if (shouldUpdateLayout) {
        await API_UPDATE_LAYOUT(sections, homepageId);
      }

      // Delete section
      const response = await API_DELETE_SECTION(homepageId, sectionId);
      if (response?.status !== 200) {
        const errorMessage = response?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      // Fetch the fresh layout STRUCTURE only — the surviving widgets'
      // metavalues are untouched by a section delete.
      const layoutResponse = await API_GET_LAYOUT(homepageId);

      return { sectionIndex, layout: layoutResponse?.data };
    },
    {
      populateCache: (result, currentData) => {
        const merged = mergeLayoutCache(currentData, result?.layout);
        // Prune configs of widgets that no longer exist in the layout
        const liveWidgetIds = new Set(
          (merged.sections || []).flatMap((section) =>
            (section.rows || []).flatMap((row) => (row.widgets || []).map((w) => w.widgetId))
          )
        );
        const widgetConfiguration = Object.fromEntries(
          Object.entries(merged.widgetConfiguration || {}).filter(
            ([id]) => liveWidgetIds.has(Number(id)) || liveWidgetIds.has(id)
          )
        );
        return { ...merged, widgetConfiguration };
      },
      revalidate: false,
      throwOnError: false
    }
  );

  const {
    trigger: updateLayout,
    isMutating: isUpdatingLayout,
    error: updateLayoutError
  } = useSWRMutation(
    layoutKey,
    async (key, { arg: { sections, isPreviewPaneDirty } }) => {
      if (!isPreviewPaneDirty) return null;

      // Remove demo widgets before saving
      const clonedSections = cloneDeep(sections);

      const response = await API_UPDATE_LAYOUT(clonedSections, homepageId);
      if (response?.status !== 200) {
        throw new Error(response?.data?.message || toastMessageConstants.SOMETHING_WENT_WRONG);
      }

      return response?.data;
    },
    {
      populateCache: (result, currentData) => mergeLayoutCache(currentData, result),
      revalidate: false,
      throwOnError: false
    }
  );

  const {
    trigger: discardChanges,
    isMutating: isDiscardingChanges,
    error: discardChangesError
  } = useSWRMutation(
    layoutKey,
    async () => {
      const response = await API_REVERT_UNPUBLISHED_CHANGES(homepageId);
      const { data } = response;

      if (response?.status !== 200) {
        const errorMessage = data?.message || toastMessageConstants.SOMETHING_WENT_WRONG;
        throw new Error(errorMessage);
      }

      return data;
    },
    {
      // Single revalidation (useSWRMutation default) — discard needs the
      // server's reverted layout + widget configs.
      throwOnError: false
    }
  );

  return {
    addSection,
    addTopFixedSection,
    deleteSection,
    updateLayout,
    discardChanges,
    isLoading:
      isAddingSection ||
      isAddingTopSection ||
      isDeletingSection ||
      isUpdatingLayout ||
      isDiscardingChanges,
    isAddingSection,
    isAddingTopSection,
    isDeletingSection,
    isUpdatingLayout,
    isDiscardingChanges,
    errors: {
      addSectionError,
      addTopSectionError,
      deleteSectionError,
      updateLayoutError,
      discardChangesError
    }
  };
}
